import { useState, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isIOS } from '../../services/native';
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownLeft,
  Plus,
  ArrowRight,
  ChevronRight,
  Calculator,
  Percent,
  Download,
  Book,
  BookOpen,
  CalendarDays,
  Building2,
  BarChart3,
  Palette,
  AlertTriangle,
  Repeat,
  Lock,
  Sparkles,
  Wallet,
  Banknote,
  CreditCard,
  MoreHorizontal,
  Wifi,
  Bell,
  Info, ScanLine } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { usePWA } from '../../context/PWAContext';
import { useTheme } from '../../context/ThemeContext';
import { useSummaryQuery, useCategoryBreakdownQuery, useTransactionsQuery, useStatsQuery, useAccountsWithBalancesQuery, useCategoriesQuery, useNotificationUnreadCountQuery, useMonthlyTrendQuery } from '../../hooks/useQueries';
import CategoryPieChart from './CategoryPieChart';
import MonthlyTrendChart from './MonthlyTrendChart';
import InsightsModal from '../insights/InsightsModal';
import { DashboardSkeleton } from '../common/Skeleton';
import { BUDGET_ALERT_THRESHOLD } from '../../utils/constants';
import { getIconComponent } from '../common/IconPicker';
import ScanPayCard from '../pay/ScanPayCard';
import { isScanPayAvailable } from '../../utils/upi';

export default function DashboardSimple() {
  const { user, hasEncryptionKey } = useAuth();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { canInstall, installPWA } = usePWA();
  const { isModernUI } = useTheme();
  const scanPayAvailable = isScanPayAvailable(config);
  const navigate = useNavigate();
  const isTaxUser = user?.email === import.meta.env.VITE_TAX_ALLOWED_EMAIL;
  const financialYear = user?.financialYear || '2025-26';
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  // Info tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [txFilter, setTxFilter] = useState('all');
  const tooltipTimeout = useRef(null);
  const toggleTooltip = useCallback((id) => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    if (activeTooltip === id) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(id);
      tooltipTimeout.current = setTimeout(() => setActiveTooltip(null), 3000);
    }
  }, [activeTooltip]);

  // Compute current month date range for filtering
  const currentMonthDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    // Pad month/day to avoid timezone issues with toISOString()
    const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const endDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  }, []);

  // React Query hooks - data is cached automatically
  const { data: summary, isLoading: summaryLoading } = useSummaryQuery(
    currentMonthDates,
    { enabled: hasEncryptionKey }
  );

  // Category breakdown filtered to current month
  const { data: categoryBreakdown } = useCategoryBreakdownQuery(
    currentMonthDates,
    { enabled: hasEncryptionKey }
  );

  const { data: transactionData, isLoading: transactionsLoading } = useTransactionsQuery(
    { limit: 5, sortBy: 'date', sortOrder: 'desc', startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate },
    { enabled: hasEncryptionKey }
  );

  // Fetch stats for budget alerts (thisMonth = calendar month)
  const { data: statsData } = useStatsQuery(
    { type: 'expense', period: 'thisMonth' },
    { enabled: hasEncryptionKey }
  );

  // Fetch monthly trend data for chart
  const { data: trendData } = useMonthlyTrendQuery({ period: 'thisMonth' }, {
    enabled: hasEncryptionKey
  });

  // Fetch accounts with balances for account breakdown
  const { data: accounts = [] } = useAccountsWithBalancesQuery({
    enabled: hasEncryptionKey
  });

  // Fetch categories for icons
  const { data: categories = [] } = useCategoriesQuery({
    enabled: hasEncryptionKey
  });

  // Fetch notification unread count
  const { data: unreadCount = 0 } = useNotificationUnreadCountQuery({
    enabled: hasEncryptionKey
  });

  // Helper to get category info (icon, color)
  const getCategoryInfo = (categoryName, type) => {
    const category = categories.find(c => c.name === categoryName && c.type === type);
    if (category) {
      return {
        icon: getIconComponent(category.icon),
        color: category.color || (type === 'income' ? '#22c55e' : '#ef4444')
      };
    }
    // Fallback for transfer or unknown
    return {
      icon: type === 'transfer' ? ArrowRight : (type === 'income' ? TrendingUp : TrendingDown),
      color: type === 'transfer' ? '#6366f1' : (type === 'income' ? '#22c55e' : '#ef4444')
    };
  };

  const allRecentTransactions = transactionData?.transactions?.slice(0, 20) || [];
  const recentTransactions = txFilter === 'all'
    ? allRecentTransactions.slice(0, 5)
    : allRecentTransactions.filter(t => t.type === txFilter).slice(0, 5);
  const loading = summaryLoading;

  // Calculate budget alerts (categories at 90%+ or over budget)
  const budgetAlerts = useMemo(() => {
    if (!statsData?.categoryBreakdown?.expense) return [];

    const alerts = [];
    Object.entries(statsData.categoryBreakdown.expense).forEach(([category, data]) => {
      if (data.budget && data.budget > 0) {
        const percentage = Math.round((data.total / data.budget) * 100);
        if (percentage >= BUDGET_ALERT_THRESHOLD) {
          alerts.push({
            name: category,
            spent: data.total,
            budget: data.budget,
            budgetPeriod: data.budgetPeriod || 'monthly',
            percentage,
            isOverBudget: percentage > 100,
            color: data.color || '#6366f1',
            count: data.count
          });
        }
      }
    });

    // Sort by percentage descending (most over budget first)
    return alerts.sort((a, b) => b.percentage - a.percentage);
  }, [statsData]);

  // Filter out hidden accounts for display and balance calculations.
  // Server's canonical flag is `isHidden`; older records used
  // `isActive: false` for the same thing, so accept either.
  const visibleAccounts = accounts.filter(
    a => a.isHidden !== true && a.isActive !== false,
  );

  // Calculate total balance from account balances (includes transfers)
  const totalBalance = visibleAccounts.reduce((sum, acc) => sum + (acc.currentBalance ?? 0), 0);
  const totalIncome = (summary?.totalIncome || 0) - (isTaxUser ? (summary?.totalTDS || 0) : 0);
  const totalExpense = summary?.totalExpense || 0;

  // Show skeleton on initial load (no cached data)
  if (loading && !summary) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="xl:hidden -mx-4 -mt-8">
        {/* Header with Logo and Welcome */}
        <div className={`px-4 pt-4 pb-4 bg-surface-50 ${isIOS ? 'sticky top-0 z-30' : ''}`} style={isIOS ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' } : undefined}>
          <div className="flex items-center justify-between">
            {/* Logo and App Name */}
            <div className="flex items-center gap-2.5">
              <img
                src="/logo.png"
                alt="Hisab Journey"
                className={isModernUI ? "w-9 h-9 rounded-lg object-contain" : "w-10 h-10 rounded-xl object-contain"}
              />
              <div>
                <p className={isModernUI ? "text-[10px] text-surface-400" : "text-xs text-surface-400"}>Welcome back</p>
                <h2 className={isModernUI ? "text-base font-bold text-surface-900" : "text-lg font-bold text-surface-900"}>
                  {user?.name?.split(' ')[0] || 'User'}
                </h2>
              </div>
            </div>
            {/* FY Badge, Insights and Install Icon */}
            <div className="flex items-center gap-1.5">
              {/* Install App Icon - only show on mobile browser, not in PWA */}
              {canInstall && (
                <button
                  onClick={installPWA}
                  className={isModernUI ? "w-8 h-8 rounded-lg flex items-center justify-center bg-income-100 text-income-600 hover:bg-income-200 transition-colors" : "w-9 h-9 rounded-xl flex items-center justify-center bg-income-100 text-income-600 hover:bg-income-200 transition-colors"}
                  title="Install App"
                >
                  <Download className={isModernUI ? "w-4 h-4" : "w-5 h-5"} />
                </button>
              )}
              {/* Notifications Icon */}
              <button
                onClick={() => navigate('/notifications')}
                className={isModernUI ? "w-8 h-8 rounded-lg flex items-center justify-center bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors relative" : "w-9 h-9 rounded-xl flex items-center justify-center bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors relative"}
                title="Notifications"
              >
                <span className={isModernUI ? "text-sm" : "text-base"}>🔔</span>
                {unreadCount > 0 && (
                  <span className={isModernUI ? "absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-expense-500 text-white text-[9px] font-bold flex items-center justify-center" : "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-expense-500 text-white text-[10px] font-bold flex items-center justify-center"}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {/* Insights Icon */}
              <button
                onClick={() => setIsInsightsOpen(true)}
                className={isModernUI ? "w-8 h-8 rounded-lg flex items-center justify-center bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors" : "w-9 h-9 rounded-xl flex items-center justify-center bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors"}
                title="HJ Insights"
              >
                <span className={isModernUI ? "text-sm" : "text-base"}>✨</span>
              </button>
{/* FY Badge - DISABLED for simplicity
              <span
                className={isModernUI ? "px-2 py-1 rounded-lg text-[10px] font-bold" : "px-3 py-1.5 rounded-xl text-xs font-semibold"}
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              >
                FY {financialYear}
              </span>
*/}
            </div>
          </div>
        </div>

        {/* Balance Card - Compact */}
        <div className="px-4 pt-2">
          <div
            className="rounded-2xl p-4 shadow-lg relative overflow-hidden"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            {/* Decorative background pattern with floating animation */}
            <div className="absolute inset-0 opacity-[0.07]">
              <div
                className="absolute -right-8 -top-8 rounded-full animate-float-slow"
                style={{ backgroundColor: 'var(--accent-text)', width: '10rem', height: '7rem' }}
              />
              <div
                className="absolute -left-6 -bottom-6 w-24 h-24 rounded-full animate-float-medium"
                style={{ backgroundColor: 'var(--accent-text)' }}
              />
              <div
                className="absolute w-16 h-16 rounded-full animate-float-fast"
                style={{ backgroundColor: 'var(--accent-text)', right: '13rem', bottom: '4rem' }}
              />
            </div>

            {/* Balance Row - Total on left, Income/Expense on right */}
            <div className="relative flex items-center justify-between gap-2">
              {/* Left: Total Balance */}
              <div>
                <div className={isModernUI ? "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full mb-0.5" : "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-1"} style={{ backgroundColor: 'var(--accent-light)' }}>
                  <Wallet className={isModernUI ? "w-2.5 h-2.5" : "w-3 h-3"} style={{ color: 'var(--accent-bg)' }} />
                  <span className={isModernUI ? "text-[9px] font-semibold" : "text-[10px] font-semibold"} style={{ color: 'var(--accent-bg)' }}>Total Balance</span>
                </div>
                <p
                  className={isModernUI ? "text-xl font-bold" : "text-2xl font-bold"}
                  style={{ color: 'var(--accent-text)' }}
                >
                  {formatAmount(totalBalance)}
                </p>
              </div>

              {/* Right: Income & Expense stacked */}
              <div className={isModernUI ? "flex flex-col gap-1" : "flex flex-col gap-1.5"}>
                <div
                  className={isModernUI ? "rounded-md px-2 py-1 flex items-center gap-1.5 backdrop-blur-sm" : "rounded-lg px-3 py-1.5 flex items-center gap-2 backdrop-blur-sm"}
                  style={{ backgroundColor: 'var(--accent-light)' }}
                >
                  <div className={isModernUI ? "w-4 h-4 rounded-full bg-income-100 flex items-center justify-center flex-shrink-0" : "w-5 h-5 rounded-full bg-income-100 flex items-center justify-center flex-shrink-0"}>
                    <TrendingUp className={isModernUI ? "w-2.5 h-2.5 text-income-600" : "w-3 h-3 text-income-600"} />
                  </div>
                  <p className={isModernUI ? "text-xs font-bold" : "text-sm font-bold"} style={{ color: config.incomeColor }}>
                    {formatAmount(totalIncome)}
                  </p>
                </div>
                <div
                  className={isModernUI ? "rounded-md px-2 py-1 flex items-center gap-1.5 backdrop-blur-sm" : "rounded-lg px-3 py-1.5 flex items-center gap-2 backdrop-blur-sm"}
                  style={{ backgroundColor: 'var(--accent-light)' }}
                >
                  <div className={isModernUI ? "w-4 h-4 rounded-full bg-expense-100 flex items-center justify-center flex-shrink-0" : "w-5 h-5 rounded-full bg-expense-100 flex items-center justify-center flex-shrink-0"}>
                    <TrendingDown className={isModernUI ? "w-2.5 h-2.5 text-expense-600" : "w-3 h-3 text-expense-600"} />
                  </div>
                  <p className={isModernUI ? "text-xs font-bold" : "text-sm font-bold"} style={{ color: config.expenseColor }}>
                    {formatAmount(totalExpense)}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Cards - Inside Balance Card */}
            {visibleAccounts.length > 0 && (
              <div className={isModernUI ? "mt-2.5 relative" : "mt-3 relative"}>
                <div className={isModernUI ? "flex gap-2 overflow-x-auto pb-1 scrollbar-hide snap-x" : "flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide snap-x"}>
                  {[...visibleAccounts].sort((a, b) => (a.currentBalance ?? 0) - (b.currentBalance ?? 0)).map((account) => {
                    const isCard = account.type === 'credit_card';
                    const accountColor = account.color || '#3b82f6';
                    const AccountIcon = getIconComponent(account.icon) || (
                      account.type === 'bank' ? Building2
                      : account.type === 'wallet' ? Wallet
                      : account.type === 'cash' ? Banknote
                      : account.type === 'credit_card' ? CreditCard
                      : MoreHorizontal
                    );

                    // Credit/Debit Card Style - Modern glass card look
                    if (isCard) {
                      return (
                        <div
                          key={account._id}
                          className={isModernUI
                            ? "flex-shrink-0 w-[120px] h-[72px] rounded-xl snap-start relative overflow-hidden cursor-pointer active:scale-[0.97] transition-all duration-200"
                            : "flex-shrink-0 w-[140px] h-[82px] rounded-xl snap-start relative overflow-hidden cursor-pointer active:scale-[0.97] transition-all duration-200"
                          }
                          style={{
                            background: `linear-gradient(135deg, ${accountColor}ee 0%, ${accountColor}bb 100%)`,
                            boxShadow: `0 4px 12px ${accountColor}40`,
                          }}
                          onClick={() => navigate('/accounts')}
                        >
                          {/* Glossy overlay */}
                          <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
                          {/* Chip */}
                          <div className={isModernUI
                            ? "absolute top-2 left-2 w-4 h-3 rounded-[3px] bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm"
                            : "absolute top-2.5 left-3 w-5 h-3.5 rounded-sm bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm"
                          }>
                            <div className="absolute inset-0.5 border-t border-l border-amber-200/50 rounded-[2px]" />
                          </div>
                          {/* NFC */}
                          <div className={isModernUI ? "absolute top-2 right-2" : "absolute top-2.5 right-3"}>
                            <Wifi className={isModernUI ? "w-2.5 h-2.5 text-white/50 rotate-90" : "w-3 h-3 text-white/50 rotate-90"} />
                          </div>
                          {/* Card details */}
                          <div className={isModernUI ? "absolute bottom-2 left-2 right-2" : "absolute bottom-2.5 left-3 right-3"}>
                            <p className={isModernUI
                              ? "text-white/80 text-[7px] font-semibold uppercase tracking-wider truncate mb-0.5"
                              : "text-white/80 text-[8px] font-semibold uppercase tracking-wider truncate mb-0.5"
                            }>
                              {account.name}
                            </p>
                            <p className={isModernUI ? "text-white text-xs font-bold" : "text-white text-sm font-bold"}>
                              {formatAmount(account.currentBalance ?? 0)}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // Other account types - Clean minimal card
                    return (
                      <div
                        key={account._id}
                        className={isModernUI
                          ? "flex-shrink-0 w-[100px] h-[72px] rounded-xl p-2 snap-start relative overflow-hidden cursor-pointer active:scale-[0.97] transition-all duration-200 bg-white/15 backdrop-blur-sm border border-white/20"
                          : "flex-shrink-0 w-[115px] h-[82px] rounded-xl p-2.5 snap-start relative overflow-hidden cursor-pointer active:scale-[0.97] transition-all duration-200 bg-white/15 backdrop-blur-sm border border-white/20"
                        }
                        onClick={() => navigate('/accounts')}
                      >
                        {/* Subtle glow */}
                        <div
                          className="absolute -top-4 -right-4 w-12 h-12 rounded-full blur-xl opacity-30"
                          style={{ backgroundColor: accountColor }}
                        />
                        {/* Icon */}
                        <div
                          className={isModernUI
                            ? "w-5 h-5 rounded-md flex items-center justify-center mb-1"
                            : "w-6 h-6 rounded-lg flex items-center justify-center mb-1.5"
                          }
                          style={{ backgroundColor: `${accountColor}30` }}
                        >
                          <AccountIcon
                            className={isModernUI ? "w-3 h-3" : "w-3.5 h-3.5"}
                            style={{ color: accountColor }}
                          />
                        </div>
                        {/* Details */}
                        <p className={isModernUI
                          ? "text-white/70 text-[7px] font-medium truncate"
                          : "text-white/70 text-[8px] font-medium truncate"
                        }>
                          {account.name}
                        </p>
                        <p className={isModernUI
                          ? "text-white text-xs font-bold mt-0.5"
                          : "text-white text-sm font-bold mt-0.5"
                        }>
                          {formatAmount(account.currentBalance ?? 0)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scan & Pay - Mobile (India / INR only) */}
        {scanPayAvailable && (
          <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
            <ScanPayCard compact={isModernUI} />
          </div>
        )}

        {/* Quick Links - Mobile (icon grid) */}
        <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
          <div className="grid grid-cols-5 gap-y-3 gap-x-1">
            {[
              { to: '/transactions', emoji: '📒', label: 'Txns', color: '#8b5cf6' },
              { to: '/recurring', emoji: '🔁', label: 'Recurring', color: '#f59e0b' },
              { to: '/receivables', emoji: '💵', label: 'Receivable', color: '#10b981' },
              { to: '/debts', emoji: '🏦', label: 'Debts', color: '#ef4444' },
              { to: '/categories', emoji: '🏷️', label: 'Categories', color: '#f97316' },
              { to: '/trend', emoji: '📈', label: 'Trend', color: '#6366f1' },
              { to: '/ledger', emoji: '📖', label: 'Ledger', color: '#0ea5e9' },
              { to: '/passcode', emoji: '🔒', label: 'App Lock', color: '#64748b' },
              { to: '/settings/theme', emoji: '🎨', label: 'Theme', color: '#ec4899' },
              { to: '/configuration', emoji: '⚙️', label: 'Config', color: '#14b8a6' },
            ].map(({ to, emoji, label, color }) => (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
              >
                <div
                  className={isModernUI ? "w-10 h-10 rounded-xl flex items-center justify-center" : "w-11 h-11 rounded-xl flex items-center justify-center"}
                  style={{ backgroundColor: `${color}12` }}
                >
                  <span className={isModernUI ? "text-lg" : "text-xl"}>{emoji}</span>
                </div>
                <span className="text-[10px] font-medium text-surface-500 text-center leading-tight">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Budget Alerts - Mobile (Dynamic layout: 1=full, 2=half, 3+=scroll) */}
        {budgetAlerts.length > 0 && (
          <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
            <div className={isModernUI ? "flex items-center justify-between mb-2" : "flex items-center justify-between mb-3"}>
              <div className={isModernUI ? "flex items-center gap-1.5" : "flex items-center gap-2"}>
                <div className={isModernUI ? "w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center" : "w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center"}>
                  <AlertTriangle className={isModernUI ? "w-3.5 h-3.5 text-amber-600" : "w-4 h-4 text-amber-600"} />
                </div>
                <div>
                  <h3 className={isModernUI ? "text-xs font-bold text-surface-900" : "text-sm font-semibold text-surface-900"}>Budget Alerts</h3>
                  <p className={isModernUI ? "text-[9px] text-surface-400" : "text-[10px] text-surface-400"}>{budgetAlerts.length} categories need attention</p>
                </div>
              </div>
              <Link
                to="/stats?type=expense"
                className={isModernUI ? "text-[10px] font-semibold px-2 py-0.5 rounded-md" : "text-xs font-medium px-2.5 py-1 rounded-lg"}
                style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)' }}
              >
                Stats
              </Link>
            </div>
            {/* 1 item = full width, 2 items = grid, 3+ = horizontal scroll */}
            <div className={
              budgetAlerts.length === 1
                ? 'grid grid-cols-1 gap-2.5'
                : budgetAlerts.length === 2
                  ? 'grid grid-cols-2 gap-2.5'
                  : 'flex gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide'
            }>
              {budgetAlerts.map((alert) => {
                const isOver = alert.isOverBudget;
                const overBy = alert.spent - alert.budget;
                const progressWidth = Math.min(alert.percentage, 100);
                return (
                  <button
                    key={alert.name}
                    onClick={() => navigate('/budget-category', { state: { category: alert } })}
                    className={`${budgetAlerts.length > 2 ? 'flex-shrink-0 w-[160px]' : ''} rounded-2xl p-3 text-left transition-all active:scale-[0.98] ${
                      isOver ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: alert.color }}
                      />
                      <span className="font-semibold text-surface-900 text-sm truncate">{alert.name}</span>
                    </div>
                    {/* Progress bar */}
                    <div className={`h-1.5 rounded-full mb-2 ${isOver ? 'bg-red-200' : 'bg-amber-200'}`}>
                      <div
                        className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-amber-500'}`}
                        style={{ width: `${progressWidth}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isOver ? 'text-red-600' : 'text-amber-600'}`}>
                        {isOver ? `+${formatAmount(overBy)}` : `${alert.percentage}%`}
                      </span>
                      <span className="text-[10px] text-surface-500">
                        {formatAmount(alert.budget)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Transactions - Mobile */}
        <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
          <div className={isModernUI ? "flex items-center justify-between mb-1.5" : "flex items-center justify-between mb-2"}>
            <h3 className={isModernUI ? "text-sm font-bold text-surface-900" : "text-base font-semibold text-surface-900"}>Recent Transactions</h3>
            <Link
              to="/transactions"
              className={isModernUI ? "text-[10px] font-semibold flex items-center gap-1" : "text-xs font-medium flex items-center gap-1"}
              style={{ color: 'var(--accent-bg)' }}
            >
              See all
            </Link>
          </div>
          {/* Filter Tabs */}
          <div className="grid grid-cols-3 gap-1 p-1 bg-surface-100 rounded-xl mb-2.5">
            {[
              { value: 'all', label: 'All', activeClass: 'bg-white text-surface-900 shadow-sm' },
              { value: 'expense', label: 'Expense', activeClass: 'bg-white text-expense-600 shadow-sm' },
              { value: 'income', label: 'Income', activeClass: 'bg-white text-income-600 shadow-sm' },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setTxFilter(tab.value)}
                className={`py-1.5 rounded-lg font-medium text-xs transition-all ${
                  txFilter === tab.value ? tab.activeClass : 'text-surface-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {!transactionsLoading && recentTransactions.length === 0 ? (
            <div className={isModernUI ? "bg-white rounded-xl p-4 text-center border border-surface-100" : "bg-white rounded-2xl p-6 text-center border border-surface-100"}>
              <div className={isModernUI ? "w-11 h-11 bg-surface-100 rounded-xl flex items-center justify-center mx-auto mb-2" : "w-14 h-14 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-3"}>
                <Book className={isModernUI ? "w-5 h-5 text-surface-400" : "w-7 h-7 text-surface-400"} />
              </div>
              <p className={isModernUI ? "text-surface-600 font-semibold text-sm" : "text-surface-600 font-medium"}>No transactions yet</p>
              <p className={isModernUI ? "text-surface-400 text-xs mt-0.5" : "text-surface-400 text-sm mt-1"}>Tap + to add your first</p>
            </div>
          ) : (
            <div className={isModernUI ? "space-y-1.5" : "space-y-2.5"}>
              {recentTransactions.map((transaction) => {
                const categoryInfo = getCategoryInfo(transaction.category?.name || transaction.category, transaction.type);
                const CategoryIcon = categoryInfo.icon;
                // Format date without year
                const dateStr = formatDate(transaction.date);
                const shortDate = dateStr.replace(/[-/]\d{4}$/, '').replace(/^\d{4}[-/]/, '');

                return (
                  <div
                    key={transaction._id}
                    className={isModernUI ? "bg-white rounded-xl p-2.5 flex items-center gap-2.5 border border-surface-100 active:bg-surface-50 transition-colors" : "bg-white rounded-2xl p-3.5 flex items-center gap-3.5 border border-surface-100 active:bg-surface-50 transition-colors"}
                    onClick={() => navigate(`/transactions/${transaction._id}`)}
                  >
                    {/* Category Icon */}
                    <div
                      className={isModernUI ? "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" : "w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"}
                      style={{ backgroundColor: `${categoryInfo.color}15` }}
                    >
                      <CategoryIcon className={isModernUI ? "w-4.5 h-4.5" : "w-6 h-6"} style={{ color: categoryInfo.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={isModernUI ? "font-semibold text-surface-900 text-xs line-clamp-1" : "font-semibold text-surface-900 text-[15px] line-clamp-1"}>
                        {transaction.description}
                      </p>
                      <p className={isModernUI ? "text-[10px] text-surface-400" : "text-xs text-surface-400 mt-0.5"}>
                        {shortDate}{transaction.time && `, ${formatTime(transaction.time)}`} • {transaction.account?.name || transaction.bankName}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p
                        className={isModernUI ? "font-bold text-xs" : "font-bold text-base"}
                        style={{
                          color: transaction.type === 'income'
                            ? config.incomeColor
                            : transaction.type === 'transfer'
                              ? 'var(--accent-bg)'
                              : config.expenseColor
                        }}
                      >
                        {formatAmount(
                          transaction.type === 'income' && isTaxUser
                            ? transaction.amountInINR - (transaction.tdsAmount || 0)
                            : transaction.amountInINR
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Charts - Mobile (side by side) */}
        <div className={isModernUI ? "mt-4 px-4" : "mt-6 px-4"}>
          <div className={isModernUI ? "flex items-center gap-1.5 mb-2" : "flex items-center gap-1.5 mb-3"}>
            <h3 className={isModernUI ? "text-sm font-bold text-surface-900" : "text-base font-semibold text-surface-900"}>Category Breakdown</h3>
            <div className="relative flex items-center">
              <button
                onClick={() => toggleTooltip('simple-cat')}
                className="flex items-center text-surface-300 hover:text-surface-500 transition-colors"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              {activeTooltip === 'simple-cat' && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-surface-800 text-white text-[10px] rounded-lg whitespace-nowrap z-50 shadow-lg">
                  Showing this month only
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-surface-800 rotate-45" />
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CategoryPieChart
              data={categoryBreakdown?.income || []}
              type="income"
              title="Income"
              compact
            />
            <CategoryPieChart
              data={categoryBreakdown?.expense || []}
              type="expense"
              title="Expense"
              compact
            />
          </div>
        </div>

        {/* Monthly Trend Charts - Mobile */}
        <div className={isModernUI ? "mt-4 px-4 pb-24" : "mt-6 px-4 pb-24"}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={isModernUI ? "text-sm font-bold text-surface-900" : "text-sm font-semibold text-surface-900"}>Income vs Expense</h3>
            <Link to="/trend" className="text-xs font-medium text-primary-500 hover:text-primary-600">View All</Link>
          </div>
          <div className="bg-white rounded-xl border border-surface-200 p-2.5">
            <MonthlyTrendChart data={trendData?.monthlyTrend} combined granularity={trendData?.granularity} compact />
          </div>
        </div>

        {/* Floating Action Button - Add Transaction */}
        <button
          onClick={() => navigate('/transactions/new')}
          className="fab"
          aria-label="Add transaction"
          data-onboarding="add-transaction"
        >
          <Plus className="w-6 h-6" />
        </button>

      </div>

      {/* Desktop Layout - Original */}
      <div className="hidden xl:block space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-surface-500 text-sm font-medium">
              Welcome back
            </p>
            <h1 className="text-3xl font-bold text-surface-900 mt-1">
              {user?.name?.split(' ')[0] || 'User'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Scan & Pay (India / INR only) */}
            {scanPayAvailable && (
            <button
              onClick={() => navigate('/scan-pay')}
              className="h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              title="Pay any UPI QR. No bank linking."
            >
              <ScanLine className="w-4 h-4" />
              Scan & Pay
            </button>
            )}
            {/* Insights Icon */}
            <button
              onClick={() => setIsInsightsOpen(true)}
              className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors"
              title="HJ Insights"
            >
              <Sparkles className="w-5 h-5" />
            </button>
{/* FY Badge - DISABLED for simplicity
            <span
              className="px-4 py-2 rounded-xl text-sm font-medium"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
            >
              FY {user?.financialYear || '2025-26'}
            </span>
*/}
          </div>
        </div>

        {/* Summary Card - Compact */}
        <div
          className="rounded-2xl p-5"
          style={{ backgroundColor: 'var(--accent-bg)' }}
        >
          {/* Main Stats Row */}
          <div className="flex items-start justify-between gap-4">
            {/* Total Balance */}
            <div className="flex-shrink-0">
              <span className="text-xs font-medium opacity-70" style={{ color: 'var(--accent-text)' }}>Total Balance</span>
              <p
                className="text-3xl font-bold"
                style={{ color: 'var(--accent-text)' }}
              >
                {formatAmount(totalBalance)}
              </p>
              <p className="text-xs mt-1 opacity-60" style={{ color: 'var(--accent-text)' }}>
                {(summary?.incomeCount || 0) + (summary?.expenseCount || 0)} transactions
              </p>
            </div>

            {/* Income & Expense Cards */}
            <div className="flex gap-3">
              {/* Income Card */}
              <div
                className="rounded-xl px-4 py-3 min-w-[140px]"
                style={{ backgroundColor: 'var(--accent-light)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="w-4 h-4 text-income-500" />
                  <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>Income</span>
                </div>
                <p className="text-lg font-bold" style={{ color: config.incomeColor }}>
                  {formatAmount(totalIncome)}
                </p>
                {isTaxUser && summary?.totalTDS > 0 && (
                  <p className="text-[10px] opacity-70 mt-0.5" style={{ color: 'var(--accent-light-text)' }}>
                    Gross: {formatAmount(summary?.totalIncome || 0)}
                  </p>
                )}
              </div>

              {/* Expense Card */}
              <div
                className="rounded-xl px-4 py-3 min-w-[140px]"
                style={{ backgroundColor: 'var(--accent-light)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingDown className="w-4 h-4 text-expense-500" />
                  <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>Expense</span>
                </div>
                <p className="text-lg font-bold" style={{ color: config.expenseColor }}>
                  {formatAmount(totalExpense)}
                </p>
              </div>

              {/* TDS Card - Only for tax user */}
              {isTaxUser && summary?.totalTDS > 0 && (
                <div
                  className="rounded-xl px-4 py-3 min-w-[120px]"
                  style={{ backgroundColor: 'var(--accent-light)' }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Percent className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>TDS</span>
                  </div>
                  <p className="text-lg font-bold text-amber-500">
                    {formatAmount(summary?.totalTDS || 0)}
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Account Breakdown - Desktop (Matching mobile style) */}
        {visibleAccounts.length >= 1 && (
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium opacity-80" style={{ color: 'var(--accent-text)' }}>
                Account Balances
              </span>
              <Link
                to="/accounts"
                className="text-xs font-medium opacity-80 hover:opacity-100 transition-opacity"
                style={{ color: 'var(--accent-text)' }}
              >
                View All
              </Link>
            </div>
            {/* Account cards - matching mobile style */}
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
              {[...visibleAccounts].sort((a, b) => (a.currentBalance ?? 0) - (b.currentBalance ?? 0)).map((account) => {
                const isCard = account.type === 'credit_card';
                const accountColor = account.color || '#3b82f6';
                const AccountIcon = getIconComponent(account.icon) || (
                  account.type === 'bank' ? Building2
                  : account.type === 'wallet' ? Wallet
                  : account.type === 'cash' ? Banknote
                  : account.type === 'credit_card' ? CreditCard
                  : MoreHorizontal
                );

                // Credit/Debit Card Style - Modern glass card look (matching mobile)
                if (isCard) {
                  return (
                    <div
                      key={account._id}
                      className="flex-shrink-0 w-[160px] h-[95px] rounded-xl relative overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-200"
                      style={{
                        background: `linear-gradient(135deg, ${accountColor}ee 0%, ${accountColor}bb 100%)`,
                        boxShadow: `0 4px 12px ${accountColor}40`,
                      }}
                      onClick={() => navigate('/accounts')}
                    >
                      {/* Glossy overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
                      {/* Chip */}
                      <div className="absolute top-3 left-3 w-6 h-4 rounded-[3px] bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm">
                        <div className="absolute inset-0.5 border-t border-l border-amber-200/50 rounded-[2px]" />
                      </div>
                      {/* NFC */}
                      <div className="absolute top-3 right-3">
                        <Wifi className="w-4 h-4 text-white/50 rotate-90" />
                      </div>
                      {/* Card details */}
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider truncate mb-0.5">
                          {account.name}
                        </p>
                        <p className="text-white text-lg font-bold">
                          {formatAmount(account.currentBalance ?? 0)}
                        </p>
                      </div>
                    </div>
                  );
                }

                // Other account types - Clean minimal card (matching mobile)
                return (
                  <div
                    key={account._id}
                    className="flex-shrink-0 w-[140px] h-[95px] rounded-xl p-3 relative overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-200 bg-white/15 backdrop-blur-sm border border-white/20"
                    onClick={() => navigate('/accounts')}
                  >
                    {/* Subtle glow */}
                    <div
                      className="absolute -top-4 -right-4 w-16 h-16 rounded-full blur-xl opacity-30"
                      style={{ backgroundColor: accountColor }}
                    />
                    {/* Icon */}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                      style={{ backgroundColor: `${accountColor}30` }}
                    >
                      <AccountIcon className="w-4 h-4" style={{ color: accountColor }} />
                    </div>
                    {/* Details */}
                    <p className="text-white/70 text-[10px] font-medium truncate">
                      {account.name}
                    </p>
                    <p className="text-white text-lg font-bold mt-0.5">
                      {formatAmount(account.currentBalance ?? 0)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Budget Alerts - Desktop */}
        {budgetAlerts.length > 0 && (
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-surface-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-900">Budget Alerts</h2>
                  <p className="text-sm text-surface-500">Categories near or over budget this month</p>
                </div>
              </div>
              <Link
                to="/stats?type=expense"
                className="text-sm font-medium flex items-center gap-1 transition-colors"
                style={{ color: 'var(--accent-bg)' }}
              >
                View All Stats
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Over Budget Section */}
            {budgetAlerts.filter(a => a.isOverBudget).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                  <p className="text-sm font-medium text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Over Budget ({budgetAlerts.filter(a => a.isOverBudget).length})
                  </p>
                </div>
                <div className="divide-y divide-surface-100">
                  {budgetAlerts.filter(a => a.isOverBudget).map((alert) => {
                    const periodLabel = {
                      monthly: '/mo',
                      quarterly: '/qtr',
                      yearly: '/yr'
                    }[alert.budgetPeriod] || '/mo';

                    return (
                      <button
                        key={alert.name}
                        onClick={() => navigate('/budget-category', { state: { category: alert } })}
                        className="w-full flex items-center gap-4 p-4 hover:bg-red-50/50 transition-colors text-left"
                      >
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: alert.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-surface-900">{alert.name}</span>
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            </div>
                            <span className="font-semibold text-red-500">
                              {formatAmount(alert.spent)} / {formatAmount(alert.budget)}{periodLabel}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
                            <span>{alert.count} transactions</span>
                            <span className="text-red-500">{alert.percentage}% of budget</span>
                          </div>
                          <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-red-500" style={{ width: '100%' }} />
                          </div>
                          <p className="text-sm mt-2 text-red-500">
                            ⚠️ Over budget by {formatAmount(alert.spent - alert.budget)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Near Budget Section */}
            {budgetAlerts.filter(a => !a.isOverBudget).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
                  <p className="text-sm font-medium text-amber-600 flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-amber-500" />
                    Near Budget ({budgetAlerts.filter(a => !a.isOverBudget).length})
                  </p>
                </div>
                <div className="divide-y divide-surface-100">
                  {budgetAlerts.filter(a => !a.isOverBudget).map((alert) => {
                    const periodLabel = {
                      monthly: '/mo',
                      quarterly: '/qtr',
                      yearly: '/yr'
                    }[alert.budgetPeriod] || '/mo';

                    return (
                      <button
                        key={alert.name}
                        onClick={() => navigate('/budget-category', { state: { category: alert } })}
                        className="w-full flex items-center gap-4 p-4 hover:bg-amber-50/50 transition-colors text-left"
                      >
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: alert.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-surface-900">{alert.name}</span>
                            </div>
                            <span className="font-semibold text-amber-500">
                              {formatAmount(alert.spent)} / {formatAmount(alert.budget)}{periodLabel}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
                            <span>{alert.count} transactions</span>
                            <span className="text-amber-500">{alert.percentage}% of budget</span>
                          </div>
                          <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-500"
                              style={{ width: `${alert.percentage}%` }}
                            />
                          </div>
                          <p className="text-sm mt-2 text-amber-500">
                            ⏳ {formatAmount(alert.budget - alert.spent)} remaining - try to reduce spending
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Breakdown Charts - Side by side */}
        <div className="grid grid-cols-2 gap-6">
          <CategoryPieChart
            data={categoryBreakdown?.income || []}
            type="income"
            title="Income by Category"
          />
          <CategoryPieChart
            data={categoryBreakdown?.expense || []}
            type="expense"
            title="Expense by Category"
          />
        </div>

        {/* Monthly Trend Charts - Desktop */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold" style={{ color: config.incomeColor || '#10b981' }}>Income Trend</h2>
              <Link to="/trend" className="text-sm font-medium text-primary-500 hover:text-primary-600">View All</Link>
            </div>
            <MonthlyTrendChart data={trendData?.monthlyTrend} type="income" granularity={trendData?.granularity} />
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold" style={{ color: config.expenseColor || '#ef4444' }}>Expense Trend</h2>
              <Link to="/trend" className="text-sm font-medium text-primary-500 hover:text-primary-600">View All</Link>
            </div>
            <MonthlyTrendChart data={trendData?.monthlyTrend} type="expense" granularity={trendData?.granularity} />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Transactions */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-surface-100">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-surface-900">Recent Transactions</h2>
                <div className="relative flex items-center">
                  <button
                    onClick={() => toggleTooltip('simple-desktop-tx')}
                    className="flex items-center text-surface-300 hover:text-surface-500 transition-colors"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                  {activeTooltip === 'simple-desktop-tx' && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-surface-800 text-white text-[10px] rounded-lg whitespace-nowrap z-50 shadow-lg">
                      Showing this month only
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-surface-800 rotate-45" />
                    </div>
                  )}
                </div>
              </div>
              <Link
                to="/transactions"
                className="text-sm text-surface-500 hover:text-surface-900 font-medium flex items-center gap-1 transition-colors"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {!transactionsLoading && recentTransactions.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-16 h-16 bg-theme-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Book className="w-8 h-8 text-theme-400" />
                </div>
                <p className="text-surface-900 font-medium">No transactions yet</p>
                <p className="text-surface-500 text-sm mt-1">Start by adding your first transaction</p>
                <Link
                  to="/transactions"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-theme-600 text-white rounded-xl text-sm font-medium hover:bg-theme-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Transaction
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-surface-100">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction._id}
                    className="flex items-center justify-between p-4 hover:bg-surface-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          transaction.type === 'income'
                            ? 'bg-income-100'
                            : 'bg-expense-100'
                        }`}
                      >
                        {transaction.type === 'income' ? (
                          <ArrowUpRight className="w-5 h-5 text-income-600" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-expense-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-surface-900">
                          {transaction.description}
                        </p>
                        <p className="text-sm text-surface-500">
                          {formatDate(transaction.date)}{transaction.time && `, ${formatTime(transaction.time)}`} • {transaction.account?.name || transaction.bankName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className="font-semibold text-lg"
                        style={{
                          color: transaction.type === 'income' ? config.incomeColor : config.expenseColor
                        }}
                      >
                        {formatAmount(
                          transaction.type === 'income' && isTaxUser
                            ? transaction.amountInINR - (transaction.tdsAmount || 0)
                            : transaction.amountInINR
                        )}
                      </p>
                      {isTaxUser && transaction.type === 'income' && transaction.tdsAmount > 0 && (
                        <>
                          <p className="text-xs text-surface-400">
                            TDS: {formatAmount(transaction.tdsAmount)}
                          </p>
                          <p className="text-xs text-surface-500">
                            Total: {formatAmount(transaction.amountInINR)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions - Compact Cards */}
          <div className="bg-white rounded-2xl border border-surface-200 p-5">
            <h2 className="text-lg font-semibold text-surface-900 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/transactions"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-income-100 rounded-lg flex items-center justify-center group-hover:bg-income-200 transition-colors">
                  <TrendingUp className="w-5 h-5 text-income-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Transactions</h3>
                  <p className="text-xs text-surface-500 truncate">Income & expenses</p>
                </div>
              </Link>

              <Link
                to="/stats"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Stats</h3>
                  <p className="text-xs text-surface-500 truncate">Category insights</p>
                </div>
              </Link>

              <Link
                to="/recurring"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <Repeat className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Recurring</h3>
                  <p className="text-xs text-surface-500 truncate">Scheduled transactions</p>
                </div>
              </Link>

              <Link
                to="/ledger"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Ledger</h3>
                  <p className="text-xs text-surface-500 truncate">Account statements</p>
                </div>
              </Link>

              <Link
                to="/receivables"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-income-100 rounded-lg flex items-center justify-center group-hover:bg-income-200 transition-colors">
                  <ArrowDownLeft className="w-5 h-5 text-income-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Receivables</h3>
                  <p className="text-xs text-surface-500 truncate">Money to receive</p>
                </div>
              </Link>

              <Link
                to="/debts"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-expense-100 rounded-lg flex items-center justify-center group-hover:bg-expense-200 transition-colors">
                  <ArrowUpRight className="w-5 h-5 text-expense-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Debts</h3>
                  <p className="text-xs text-surface-500 truncate">Money to pay</p>
                </div>
              </Link>

              <Link
                to="/passcode"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-surface-100 rounded-lg flex items-center justify-center group-hover:bg-surface-200 transition-colors">
                  <Lock className="w-5 h-5 text-surface-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">App Lock</h3>
                  <p className="text-xs text-surface-500 truncate">Passcode settings</p>
                </div>
              </Link>

              <Link
                to="/settings/theme"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <Palette className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Theme</h3>
                  <p className="text-xs text-surface-500 truncate">Theme settings</p>
                </div>
              </Link>

              {isTaxUser && (
                <Link
                  to="/tax"
                  className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                    style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                  >
                    <Calculator className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-surface-900 text-sm">Tax Report</h3>
                    <p className="text-xs text-surface-500 truncate">View calculations</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Insights Modal */}
      <InsightsModal
        isOpen={isInsightsOpen}
        onClose={() => setIsInsightsOpen(false)}
      />
    </>
  );
}
